import { ApiException } from '../errors/api.exception';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoggingHandler } from '../errors/logging.service';
import { environment } from '@app-environments/environment';

@Injectable({ providedIn: 'root' })
export class ApiRequest {
  private organizationId: number;

  constructor(
    private http: HttpClient,
    protected loggingHandler: LoggingHandler
  ) {}

  setOrganisationId(organizationId: number): void {
    this.organizationId = organizationId;
  }

  public async post<TRequest, TResponse>(
    request: TRequest,
    url: string,
    headers: any = null,
    withOrganizationId = true
  ): Promise<any> {
    try {
      const ClientId = this.loggingHandler.getClientId();
      if (this.organizationId && withOrganizationId) {
        if (request instanceof FormData) {
          request.append('organizationId', this.organizationId.toString());
        } else {
          request = {
            ...request,
            organizationId: this.organizationId,
          };
        }
      }
      const response$: Observable<TResponse> = await this.http.post<TResponse>(
        url,
        request,
        {
          headers: { ...headers, ClientId, 'X-Api-Key': environment.xApiKey },
          withCredentials: true,
        }
      );
      return !!response$ && (await lastValueFrom(response$));
    } catch (e) {
      this.throwApiException(e, { url }, { request }, { headers });
    }
  }

  public async get<TResponse>(url: string, headers: any = null): Promise<any> {
    try {
      const ClientId = this.loggingHandler.getClientId();
      const newUrl = new URL(url);
      this.organizationId &&
        newUrl.searchParams.set('organizationId', String(this.organizationId));
      const response$: Observable<TResponse> = await this.http.get<TResponse>(
        newUrl.toString(),
        {
          headers: { ...headers, ClientId, 'X-Api-Key': environment.xApiKey },
          withCredentials: true,
        }
      );
      return response$ && (await lastValueFrom(response$));
    } catch (e) {
      this.throwApiException(e, { url }, { headers });
    }
  }

  public async download(
    url: string
  ): Promise<
    { file: ArrayBuffer; filename: string; type: string } | undefined
  > {
    try {
      const ClientId = this.loggingHandler.getClientId();
      const newUrl = new URL(url);
      this.organizationId &&
        newUrl.searchParams.set('organizationId', String(this.organizationId));
      const response$ = await this.http.get(newUrl.toString(), {
        headers: { ClientId, 'X-Api-Key': environment.xApiKey },
        responseType: 'arraybuffer',
        withCredentials: true,
        observe: 'response',
      });
      const data = response$ && (await lastValueFrom(response$));
      const contentDisposition = data.headers.get('Content-Disposition');

      const filenameIndex = contentDisposition?.indexOf('filename=');
      const filename =
        contentDisposition && filenameIndex
          ? contentDisposition.substring(filenameIndex + 9).split(';')[0]
          : '';
      return {
        file: data.body as ArrayBuffer,
        type: (data?.headers?.get('content-type') as string) ?? '',
        filename: filename
          .trim()
          .replace(/^['"]+|['"]+$/g, '')
          .trim(),
      };
    } catch (e) {
      this.throwApiException(e, { url });
      return;
    }
  }

  public async downloadPost<TRequest>(
    url: string,
    request: TRequest
  ): Promise<any> {
    try {
      const ClientId = this.loggingHandler.getClientId();
      const data = this.organizationId
        ? { ...request, organizationId: this.organizationId }
        : request;
      const response$ = await this.http.post(url, data, {
        headers: { ClientId, 'X-Api-Key': environment.xApiKey },
        responseType: 'arraybuffer',
        withCredentials: true,
      });
      return response$ && (await lastValueFrom(response$));
    } catch (e) {
      this.throwApiException(e, { url }, { request });
    }
  }

  public upload<TRequest, TResponse>(
    request: TRequest,
    url: string,
    headers: any = null
  ): Observable<any> {
    const ClientId = this.loggingHandler.getClientId();
    const data = this.organizationId
      ? { ...request, organizationId: this.organizationId }
      : request;
    return this.http
      .post(url, data, {
        headers: { ...headers, ClientId, 'X-Api-Key': environment.xApiKey },
        observe: 'events',
        reportProgress: true,
        withCredentials: true,
      })
      .pipe(catchError(err => throwError(err))) as Observable<TResponse>;
  }

  private throwApiException(e: any, ...args: any[]): void {
    const errorInformation: any[] = [];
    args.forEach(el => errorInformation.push(el));
    const context = JSON.stringify(errorInformation);
    throw new ApiException(
      `${this.formatHttpError(e)} Context: ${context}`,
      e.error?.code,
      e.error?.description,
      e.error?.meta,
      e.status
    );
  }

  private formatHttpError(e: any): string {
    if (!(e instanceof HttpErrorResponse)) {
      return JSON.stringify({
        kind: 'NonHttpError',
        message: e?.message ?? String(e),
        error: e?.error ?? e,
        timestamp: new Date().toISOString(),
        correlationId: this.loggingHandler.getClientId() || null,
      });
    }

    return JSON.stringify({
      kind: 'HttpErrorResponse',
      status: e.status,
      statusText: e.statusText,
      url: e.url,
      name: e.name,
      message: e.message,
      ok: e.ok,
      response: this.formatHttpErrorBody(e.error),
      timestamp: new Date().toISOString(),
      correlationId: this.loggingHandler.getClientId() || null,
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      visibilityState:
        typeof document !== 'undefined' ? document.visibilityState : undefined,
    });
  }

  private formatHttpErrorBody(error: unknown): unknown {
    if (error == null) {
      return null;
    }

    if (
      typeof ProgressEvent !== 'undefined' &&
      error instanceof ProgressEvent
    ) {
      return {
        type: 'ProgressEvent',
        eventType: error.type,
        loaded: error.loaded,
        total: error.total,
        lengthComputable: error.lengthComputable,
        isTrusted: error.isTrusted,
      };
    }

    if (typeof ErrorEvent !== 'undefined' && error instanceof ErrorEvent) {
      return {
        type: 'ErrorEvent',
        message: error.message,
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno,
      };
    }

    if (error instanceof Error) {
      return {
        type: error.name,
        message: error.message,
      };
    }

    try {
      return error;
    } catch {
      return String(error);
    }
  }
}
